import { Flex, Text } from '@chakra-ui/react'
import { PopUpMessageContext, useArrayFromRecord } from '@hb/react'
import {
  AssessmentInvoiceSnippet,
  capitalizeFirstLetterOfEachWord,
  DropdownField,
  FieldTypes,
  getDateString,
  INVOICES,
  PopulatedAssessment,
  PracticeInvoice,
  UNSENT_INVOICE_STATUSES,
  WithId,
} from '@hb/shared'
import { collection, query, Query, where } from 'firebase/firestore'
import { useCallback, useContext, useMemo, useState } from 'react'
import { db } from '../../backend/db/db'
import { manuallyMovePaymentToInvoice } from '../../backend/functions/invoices'
import { manuallyAddPaymentToSpecificInvoice } from '../../backend/functions/other'
import { useQuery } from '../../hooks/backend/useQuery'
import { InvoiceStatusView } from './InvoiceStatusView'

const getUnsentPracticeInvoicesQuery = (practiceId: string) =>
  query(
    collection(db, INVOICES),
    where('practiceId', '==', practiceId),
    where('status', 'in', UNSENT_INVOICE_STATUSES),
    where('archivedOn', '==', null),
  ) as Query<PracticeInvoice>

const useInvoiceField = (
  placeholder: string,
  options: WithId<PracticeInvoice>[],
  invoices: Record<string, PracticeInvoice> | null,
) =>
  useMemo<DropdownField>(
    () => ({
      type: FieldTypes.DROPDOWN,
      placeholder,
      options: options.map(unsentInvoice => ({
        id: unsentInvoice.id,
        text: `${capitalizeFirstLetterOfEachWord(unsentInvoice.status.split('-').join(' '))} - ${
          unsentInvoice.scheduledFor
            ? `Scheduled for ${getDateString(unsentInvoice.scheduledFor, 'short', true)}`
            : `Due on ${getDateString(unsentInvoice.dueDate, 'short', true)}`
        }`,
      })),
      optional: true,
      renderOption: ({ id }) => (
        <Flex p={2} flexFlow="column">
          {invoices?.[id] ? <InvoiceStatusView invoice={invoices?.[id]} /> : null}
          <Text fontSize="xs" opacity={0.7}>
            ID: {id}
          </Text>
        </Flex>
      ),
    }),
    [invoices, options, placeholder],
  )

const useUnsentInvoices = (practiceId: string | null | undefined) => {
  const unsentInvoicesQuery = useMemo(
    () => (practiceId ? getUnsentPracticeInvoicesQuery(practiceId) : null),
    [practiceId],
  )
  return useQuery<PracticeInvoice>(unsentInvoicesQuery)
}

export interface MovePaymentProps {
  invoice: WithId<PracticeInvoice | AssessmentInvoiceSnippet> | null
  assessmentId: string | null | undefined
  paymentId: string
  initialFromId?: string | null
  initialToId?: string | null
  onClose: () => void
}
export const useMovePayment = ({
  invoice,
  assessmentId,
  paymentId,
  initialFromId,
  initialToId,
  onClose,
}: MovePaymentProps) => {
  const { processResponse, showError } = useContext(PopUpMessageContext)
  const { practiceId } = invoice ?? {}

  const { data: unsentInvoices } = useUnsentInvoices(practiceId)

  const [moveToId, setMoveToId] = useState<string | null>(initialToId ?? null)
  const [moveFromId, setMoveFromId] = useState<string | null>(initialFromId ?? null)

  const unsentInvoicesArr = useArrayFromRecord(unsentInvoices ?? {})

  const toInvoiceOptions = useMemo(
    () =>
      unsentInvoicesArr.filter(unsentInvoice => {
        if (moveFromId && unsentInvoice.id === moveFromId) return false
        return true
      }),
    [unsentInvoicesArr, moveFromId],
  )

  const fromInvoiceOptions = useMemo(
    () => unsentInvoicesArr.filter(unsentInvoice => unsentInvoice.id !== moveToId),
    [unsentInvoicesArr, moveToId],
  )

  const toInvoiceField = useInvoiceField('To Invoice', toInvoiceOptions, unsentInvoices)
  const fromInvoiceField = useInvoiceField('From Invoice', fromInvoiceOptions, unsentInvoices)

  const [moveLoading, setMoveLoading] = useState(false)
  const canMove = useMemo(() => !!moveToId && !!moveFromId, [moveToId, moveFromId])
  const onMove = useCallback(async () => {
    if (!moveToId) return
    if (!moveFromId) return
    if (moveToId === moveFromId) {
      showError('Error moving payment', 'You cannot move a payment to the same invoice')
      return
    }

    if (!assessmentId) {
      processResponse({ error: 'Assessment ID not found' })
      return
    }
    setMoveLoading(true)
    try {
      await manuallyMovePaymentToInvoice({
        assessmentId,
        fromInvoiceId: moveFromId,
        toInvoiceId: moveToId,
        paymentId,
      })
      processResponse({ success: 'Moved payment' })
      onClose()
    } catch (err: any) {
      console.error(err)
      processResponse({ error: err?.message || 'Error moving payment' })
    }
    setMoveLoading(false)
  }, [moveToId, assessmentId, paymentId, processResponse, onClose, moveFromId, showError])

  return useMemo(
    () => ({
      moveToId,
      moveFromId,
      setMoveToId,
      setMoveFromId,
      toInvoiceField,
      hasMultipleUnsent: unsentInvoicesArr.length > 1,
      fromInvoiceField,
      moveLoading,
      canMove,
      onMove,
    }),
    [
      moveToId,
      moveFromId,
      setMoveToId,
      setMoveFromId,
      toInvoiceField,
      fromInvoiceField,
      moveLoading,
      canMove,
      onMove,
      unsentInvoicesArr,
    ],
  )
}

export interface AddPaymentProps {
  assessment: PopulatedAssessment | null
  assessmentId: string | null | undefined
  paymentId: string
  initialToId?: string | null
  onClose: () => void
}
export const useAddPayment = ({
  assessment,
  assessmentId,
  paymentId,
  initialToId,
  onClose,
}: AddPaymentProps) => {
  const { processResponse, showError } = useContext(PopUpMessageContext)
  const { midwifeId: practiceId } = assessment ?? {}

  const { data: unsentInvoices } = useUnsentInvoices(practiceId)

  const [addToId, setAddToId] = useState<string | null>(initialToId ?? null)

  const unsentInvoicesArr = useArrayFromRecord(unsentInvoices ?? {})

  const toInvoiceField = useInvoiceField('To Invoice', unsentInvoicesArr, unsentInvoices)

  const [addLoading, setAddLoading] = useState(false)
  const canAdd = useMemo(() => !!addToId, [addToId])
  const onAdd = useCallback(async () => {
    if (!addToId) return

    if (!assessmentId) {
      processResponse({ error: 'Assessment ID not found' })
      return
    }
    setAddLoading(true)
    try {
      await manuallyAddPaymentToSpecificInvoice({
        assessmentId,
        invoiceId: addToId,
        paymentId,
      })
      processResponse({ success: 'Added payment' })
      onClose()
    } catch (err: any) {
      console.error(err)
      showError('Error adding payment', err?.message || 'Unknown error')
    }
    setAddLoading(false)
  }, [addToId, assessmentId, paymentId, processResponse, onClose, showError])

  return useMemo(
    () => ({
      addToId,
      setAddToId,
      toInvoiceField,
      hasUnsentInvoices: !!unsentInvoicesArr.length,
      addLoading,
      canAdd,
      onAdd,
    }),
    [addToId, setAddToId, toInvoiceField, addLoading, canAdd, onAdd, unsentInvoicesArr],
  )
}
